import 'react';
import { body, Heading } from '@lendi-ui/typography';
import Overlay from '@lendi-ui/overlay';
import { Fade } from '@lendi-ui/transition';
import FocusLock from 'react-focus-lock';
import Close from '@lendi-ui/icon/Close';
import styled, { css } from 'styled-components';
import { bg, color } from '@lendi-ui/color';
import { gte } from '@lendi-ui/breakpoint';
import { py, pr, pl, pb, pt, px, p } from '@lendi-ui/spacing';
import { depth } from '@lendi-ui/depth';
import { select } from '@lendi-ui/theme';
import { deriveSize, normalise } from '@lendi-ui/utils';
import { jsx, jsxs } from 'react/jsx-runtime';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;
var widthBySize = {
  lg: deriveSize(37.5),
  // 600px @ desktop res
  md: deriveSize(25),
  // 400px @ desktop res
  sm: deriveSize(22) // 352px @ desktop res
};

var Wrapper = styled.div.withConfig({
  displayName: "indexstyle__Wrapper",
  componentId: "lui__sc-1jbm7an-0"
})(["", ";position:fixed;z-index:20;width:600px;"], normalise);
var SrOnlySpan = styled.span.withConfig({
  displayName: "indexstyle__SrOnlySpan",
  componentId: "lui__sc-1jbm7an-1"
})(["border:0 !important;clip:rect(1px,1px,1px,1px) !important;clip-path:inset(50%) !important;height:1px !important;overflow:hidden !important;padding:0 !important;position:absolute !important;width:1px !important;white-space:nowrap !important;"]);
var Content = styled.div.withConfig({
  displayName: "indexstyle__Content",
  componentId: "lui__sc-1jbm7an-2"
})([""]);
var Footer = styled.div.withConfig({
  displayName: "indexstyle__Footer",
  componentId: "lui__sc-1jbm7an-3"
})([""]);
var HeaderWrapper = styled.div.withConfig({
  displayName: "indexstyle__HeaderWrapper",
  componentId: "lui__sc-1jbm7an-4"
})(["", ";min-height:", ";margin-bottom:-1px;border-bottom:1px solid ", ";", " ", ";", ";", ";display:flex;flex:0 1 auto;", ";"], bg('shade.50'), deriveSize(4), color('shade.50'), gte('tablet')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    border-radius: ", " ", " 0 0;\n  "])), select('borderRadius'), select('borderRadius')), py('xxs'), pr('xxl'), pl('md'), _ref => {
  var {
    size = 'md'
  } = _ref;
  return css(["", ""], gte('tablet')(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      ", ";\n    "])), size === 'lg' && pl('lg')));
});
var TitlesWrapper = styled.div.withConfig({
  displayName: "indexstyle__TitlesWrapper",
  componentId: "lui__sc-1jbm7an-5"
})(["margin:auto 0;"]);
var Subtitle = styled.p.withConfig({
  displayName: "indexstyle__Subtitle",
  componentId: "lui__sc-1jbm7an-6"
})(["overflow-wrap:normal;", ";margin:0;", ""], body({
  size: 'xs'
}), pb('xxxs'));
var Container = styled.div.withConfig({
  displayName: "indexstyle__Container",
  componentId: "lui__sc-1jbm7an-7"
})(["", ";"], _ref2 => {
  var {
    size = 'md',
    isHeaderFixed
  } = _ref2;
  return css(["display:", ";flex-direction:column;opacity:0.97;z-index:10;height:100%;width:100%;position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);border-radius:0;", ";", " ", "{", " ", " ", " ", " flex:1 1 auto;min-height:0;overflow:auto;}", "{", " ", " border-radius:0;", " ", " flex:0 1 auto;}"], props => props.isVisible ? 'flex;' : 'none', bg('shade.25'), gte('tablet')(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      width: ", ";\n      border-radius: ", ";\n      height: initial;\n      max-height: 85vh;\n      opacity: 1;\n    "])), widthBySize[size], select('borderRadius')), Content, pt(isHeaderFixed ? 'md' : 'xxl'), pb('xl'), px('md'), gte('tablet')(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n        ", ";\n      "])), size === 'lg' && px('lg')), Footer, py('sm'), px('md'), depth(3), gte('tablet')(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n        border-bottom-left-radius: ", ";\n        border-bottom-right-radius: ", ";\n        ", ";\n      "])), select('borderRadius'), select('borderRadius'), size === 'lg' && px('lg')));
});
var CloseButton = styled.button.withConfig({
  displayName: "indexstyle__CloseButton",
  componentId: "lui__sc-1jbm7an-8"
})(["border:0;border-radius:0;background:none;white-space:nowrap;text-decoration:none;margin:0;cursor:pointer;", ";right:0;box-sizing:content-box;width:32px;height:32px;position:absolute;"], p('sm'));

var _excluded = ["isVisible", "size", "onHide", "isHeaderFixed", "children"];
var Header = props => {
  var _props$size;
  var size = (_props$size = props.size) !== null && _props$size !== void 0 ? _props$size : 'sm';
  return /*#__PURE__*/jsx(HeaderWrapper, {
    children: /*#__PURE__*/jsxs(TitlesWrapper, {
      children: [props.title && /*#__PURE__*/jsx(Heading, {
        size: size,
        children: props.title
      }), props.subtitle && /*#__PURE__*/jsx(Subtitle, {
        children: props.subtitle
      })]
    })
  });
};
var Modal = _ref => {
  var {
      isVisible: show,
      size,
      onHide,
      isHeaderFixed = false,
      children
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/jsx(FocusLock, {
    disabled: !show,
    returnFocus: true,
    children: /*#__PURE__*/jsxs(Wrapper, {
      children: [/*#__PURE__*/jsx(Overlay, {
        isVisible: show,
        zIndex: 5,
        onHide: onHide
      }), /*#__PURE__*/jsx(Fade, {
        isActive: show,
        children: /*#__PURE__*/jsxs(Container, _objectSpread2(_objectSpread2({
          isVisible: show,
          size: size,
          isHeaderFixed: isHeaderFixed,
          className: "modal-container"
        }, otherProps), {}, {
          children: [/*#__PURE__*/jsxs(CloseButton, {
            type: "button",
            onClick: onHide,
            children: [/*#__PURE__*/jsx(SrOnlySpan, {
              children: "Close modal"
            }), /*#__PURE__*/jsx(Close, {
              color: "shade.300",
              width: "2rem",
              height: "2rem",
              "aria-hidden": "true"
            })]
          }), children]
        }))
      })]
    })
  });
};
Modal.Content = Content;
Modal.Footer = Footer;
Modal.Header = Header;

export { Header, Modal, Modal as default };
